/**
 * The alert-rule slice of the wire — Task U16 (Tier-3 live-tenant readiness),
 * consuming `GET`/`POST /api/v1/alert-rules` and `PATCH /api/v1/alert-rules/{id}`
 * (`src/modules/alerting/routes.ts`, `openapi.yaml`'s `AlertRule`,
 * `AlertRuleCreateRequest` and `AlertRulePatchRequest`).
 *
 * Hand-written to mirror the contract, the same discipline `policies.ts`,
 * `retention.ts` and `permission-grants.ts` state: transcribe what the screen reads,
 * not the whole document.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THERE IS NO ACTIVATE ENDPOINT. ACTIVATION IS A STATUS-ONLY `PATCH`.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The plan told U16 to check this before trusting a button, and the answer is the
 * sharper version of U15's finding. `/alert-rules` publishes three operations —
 * `GET`, `POST` and `PATCH /{id}` — and **no** `/{id}/activate`. Putting a rule into
 * force is `PATCH /{id}` with the body `{"status":"active"}` and a mandatory
 * `If-Match` carrying the current version integer. The body is not optional
 * decoration: `src/modules/alerting/service.ts` branches on the patch being
 * **status-only** (`keys.length === 1 && keys[0] === 'status'`) to move the existing
 * row instead of minting a version, and `AlertRulePatchRequest` is `minProperties: 1`,
 * so an empty body is a `422 WORKFORCE_VALIDATION_FAILED` rather than an activation.
 *
 * U14's `activate` sends `bodyWith({}, precondition)`, which on an `if-match` surface
 * is exactly `{}`. So on this backend the shared `activate` cannot activate anything:
 * it would send the one body the server refuses. `alertRuleActivatePath` below exists
 * to say that in code rather than in a comment — it is what this surface hands
 * `activation.path`, and it throws. The screen puts `status` in the edit form and the
 * ordinary Save sends `PATCH {"status":"active"}` with the row's own `If-Match`, which
 * is the request the backend documents.
 *
 * ── The version IS on the list row, unlike `/policies` ─────────────────────
 *
 * `renderAlertRule` puts `version` on every row of `GET /alert-rules`
 * (`src/modules/alerting/service.ts`), and `If-Match` wants that same integer
 * (`ifMatchVersion`, `routes.ts`). So `preconditionOf` has a token to return from a
 * list row without the extra read `/policies` needed, and U14's note that U15's
 * missing row token was "the exception rather than the rule" is confirmed here.
 *
 * A conflict answers `{ expected, actual }` — no `current_etag` — where `actual` is
 * the winner's version. `useVersionedConfig`'s `storedTokenOf` already reads `actual`,
 * so the conflict names the newer version immediately; the stored *values* still cost
 * a reload press, because the `409` body carries no representation.
 *
 * ── Two closed vocabularies, and why they are closed here ──────────────────
 *
 * `ALERT_ACTION_PARAMETERS` and `ALERT_TRIGGER_FIELDS` are exhaustive `Record`s over
 * the wire enums. A value added to `openapi.yaml` and not here is a **compile error**
 * at the record, and `alert-rules.test.tsx` reads both enums out of `openapi.yaml` at
 * test time so a value added here and not there is a **red test**. Neither list is
 * retyped from memory in the screen, and no control anywhere in the form takes a
 * free-text value where the backend takes an enum.
 */

import type { WorkforcePermission } from './types';

export const ALERT_RULES_PATH = '/api/v1/alert-rules';

export const alertRulePath = (id: string): string => `${ALERT_RULES_PATH}/${id}`;

/**
 * The versions of one rule, newest first.
 *
 * `GET /alert-rules` is the catalogue of every rule and every version; narrowed by
 * `rule_key` it is the lineage of one rule, which is the shape `useVersionedConfig`
 * models — see `AlertRulesScreen`'s comment on why the screen switches between them.
 */
export const alertRuleKeyPath = (ruleKey: string): string =>
  `${ALERT_RULES_PATH}?rule_key=${encodeURIComponent(ruleKey)}`;

/** The code the three endpoints are authorised on (`alerting/authorizer.ts`). */
export const ALERT_RULE_PERMISSION: WorkforcePermission = 'workforce.manage_alerts';

/**
 * `activation.path` for this surface, and it refuses rather than returning a path.
 *
 * U14's `activate` would send `PATCH /alert-rules/{id}` with `{}`, which
 * `AlertRulePatchRequest`'s `minProperties: 1` refuses — an activation that cannot
 * activate. `AlertRulesScreen` never calls `state.activate`; this makes that
 * structural instead of remembered, in the shape `refuseUnlessAlertRuleAuthorizerWired`
 * uses on the server: a wiring fault that is loud beats one that sends a request the
 * server will reject with a validation error nobody will connect to a button.
 */
export function alertRuleActivatePath(): never {
  throw new Error(
    'alert-rule activation is a status-only PATCH carrying {"status":"active"}, not an ' +
      "activate endpoint. useVersionedConfig's `activate` sends an empty body, which " +
      'AlertRulePatchRequest (minProperties: 1) refuses. Set `status` on the draft and ' +
      'patch the row instead.',
  );
}

/* ─────────────────────────────────────────────────────────── the wire vocabulary */

/**
 * `AlertTrigger` — the eighteen values of `openapi.yaml`, in the CHECK's own order
 * (`src/modules/alerting/engine.ts`'s `ALERT_TRIGGERS`).
 *
 * `blocked_app`, not `blocked_application`: the database CHECK is what the enum has
 * to match, and Task 49 corrected the contract rather than accommodating it.
 */
export const ALERT_TRIGGERS = [
  'extended_idle',
  'no_active_task',
  'estimate_exceeded',
  'productivity_drop',
  'workload_overload',
  'workload_underload',
  'late_arrival',
  'early_leave',
  'missing_hours',
  'agent_offline',
  'agent_stopped',
  'agent_uninstalled',
  'unknown_device',
  'usb_unapproved',
  'bulk_file_operation',
  'blocked_app',
  'blocked_website',
  'after_hours_activity',
] as const;

export type AlertTrigger = (typeof ALERT_TRIGGERS)[number];

/** `Severity` — Master §15.2, least to most severe. */
export const ALERT_SEVERITIES = ['informational', 'low', 'medium', 'high', 'critical'] as const;

export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

/**
 * `AlertAction` — the five keys `wp_alert_rules_actions_ck` admits, and exactly the
 * five `AUTOMATION_ACTION_KEYS` of `src/modules/automation/keys.ts` that
 * `automation-repo.ts` persists in `wp_automation_runs.action_key`.
 *
 * `webhook` and `notify_employee` are deliberately not here: they were removed from
 * the contract by Task 49 because the CHECK never admitted them, and SEC-45 requires
 * an SSRF control to exist before a `webhook` action does.
 */
export const ALERT_ACTIONS = [
  'notify_manager',
  'notify_it',
  'notify_hr',
  'create_incident',
  'create_achau_task',
] as const;

export type AlertAction = (typeof ALERT_ACTIONS)[number];

/** The six dimensions `wp_alert_rules_groupby_ck` admits, in canonical order. */
export const ALERT_GROUP_BY_FIELDS = [
  'employee_id',
  'device_id',
  'department_id',
  'app_name',
  'domain',
  'achau_task_id',
] as const;

export type AlertGroupByField = (typeof ALERT_GROUP_BY_FIELDS)[number];

/** `wp_alert_rules.status`. Four values, not a boolean — see `openapi.yaml`. */
export const ALERT_RULE_STATUSES = ['draft', 'active', 'disabled', 'retired'] as const;

export type AlertRuleStatus = (typeof ALERT_RULE_STATUSES)[number];

/**
 * The two statuses `AlertRulePatchRequest` admits.
 *
 * `draft` and `retired` are unreachable by a patch — a draft is created as one, and a
 * version is retired by being superseded — so the form offers neither. Absent, not
 * disabled.
 */
export const PATCHABLE_STATUSES = ['active', 'disabled'] as const;

export type PatchableStatus = (typeof PATCHABLE_STATUSES)[number];

/** The two statuses `AlertRuleCreateRequest` admits. */
export const CREATABLE_STATUSES = ['draft', 'active'] as const;

export type CreatableStatus = (typeof CREATABLE_STATUSES)[number];

/**
 * The operators this form offers — a **subset** of `AlertCondition`'s nine.
 *
 * `in` and `not_in` take a non-empty array and `contains` a non-empty substring
 * (`src/modules/alerting/service.ts`'s `conditionSchema`). Both are on the wire and
 * both are refused by this form, because the only honest control for either is a
 * free-text box: a comma-splitting input for a set, or a substring for `contains`.
 * The backend still accepts them from any other client, which the U16 report records.
 */
export const OFFERED_OPERATORS = ['>', '>=', '<', '<=', '==', '!='] as const;

export type OfferedOperator = (typeof OFFERED_OPERATORS)[number];

/** The operators a boolean or an enumerated field may be compared with. */
export const EQUALITY_OPERATORS = ['==', '!='] as const;

/* ───────────────────────────────────────────────────────── the rule on the wire */

/** One condition of `conditions_json`. `value` is a scalar — this form sends no sets. */
export interface AlertRuleCondition {
  readonly field: string;
  readonly operator: string;
  readonly value: number | string | boolean;
}

/** `AlertRule`, narrowed to what this screen reads. */
export interface AlertRule {
  readonly id: string;
  readonly company_id: string;
  readonly rule_key: string;
  readonly name: string;
  readonly description: string | null;
  /** The compare-and-set token. Sent as `If-Match: "<version>"`. */
  readonly version: number;
  readonly trigger: AlertTrigger;
  readonly conditions: readonly AlertRuleCondition[];
  readonly threshold: number | null;
  readonly window_minutes: number | null;
  readonly group_by: readonly AlertGroupByField[];
  readonly severity: AlertSeverity;
  readonly dedup_window_minutes: number;
  readonly cooldown_minutes: number;
  readonly actions: readonly AlertAction[];
  readonly effective_from: string | null;
  readonly effective_to: string | null;
  readonly status: AlertRuleStatus;
  readonly is_system_seed: boolean;
  readonly supersedes_id: string | null;
  readonly created_at: string;
}

export interface AlertRuleListResponse {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly data: readonly AlertRule[];
}

/**
 * The keys the **edit** form may touch, and therefore the keys a `PATCH` body may
 * carry.
 *
 * `trigger` and `rule_key` are absent and that is not an oversight: neither is in
 * `AlertRulePatchRequest`, which is `additionalProperties: false`, so a draft that
 * could hold them would produce a `422` the first time somebody created a rule and
 * then edited one without reloading. U14 holds **one** draft for both write intents
 * (U15's finding), and on this backend the two intents take different field sets —
 * so the create form deliberately keeps its own state and never touches this draft.
 * See `AlertRulesScreen`.
 */
export interface AlertRuleDraft {
  readonly name: string;
  readonly description: string | null;
  readonly conditions: readonly AlertRuleCondition[];
  readonly threshold: number | null;
  readonly window_minutes: number;
  readonly group_by: readonly AlertGroupByField[];
  readonly severity: AlertSeverity;
  readonly dedup_window_minutes: number;
  readonly cooldown_minutes: number;
  readonly actions: readonly AlertAction[];
  /** The status move. A patch carrying only this one moves the row, minting nothing. */
  readonly status: PatchableStatus;
}

/** `AlertRuleCreateRequest`. Five members are required; the rest have column defaults. */
export interface AlertRuleCreateRequest {
  readonly name: string;
  readonly trigger: AlertTrigger;
  readonly conditions: readonly AlertRuleCondition[];
  readonly severity: AlertSeverity;
  readonly actions: readonly AlertAction[];
  readonly rule_key?: string;
  readonly description?: string | null;
  readonly threshold?: number | null;
  readonly window_minutes?: number;
  readonly group_by?: readonly AlertGroupByField[];
  readonly dedup_window_minutes?: number;
  readonly cooldown_minutes?: number;
  readonly status?: CreatableStatus;
}

/* ───────────────────────────────────────── the per-trigger condition vocabulary */

/** How a field's comparand is typed, and therefore which control renders for it. */
export type ConditionFieldKind = 'number' | 'boolean' | 'enum';

export interface ConditionField {
  readonly name: string;
  readonly kind: ConditionFieldKind;
  /** The closed option list for an `enum` field; empty for the other two kinds. */
  readonly options: readonly string[];
}

const numberField = (name: string): ConditionField => ({ name, kind: 'number', options: [] });
const booleanField = (name: string): ConditionField => ({ name, kind: 'boolean', options: [] });
const enumField = (name: string, options: readonly string[]): ConditionField => ({
  name,
  kind: 'enum',
  options,
});

/** `deviceLiveness()`'s three values (`src/modules/devices/status-engine.ts`). */
const LIVENESS_VALUES = ['online', 'stale', 'offline'] as const;

/** `wp_devices_status_check`'s nine values. */
const DEVICE_STATUS_VALUES = [
  'pending',
  'active',
  'offline',
  'suspended',
  'revoked',
  'uninstalled',
  'agent_outdated',
  'policy_error',
  'sync_error',
] as const;

/**
 * Which fields exist for each trigger, and for twelve of the eighteen the answer is
 * **none**.
 *
 * `FACT_LOADERS` (`src/modules/alerting/alerting-repo.ts`) ships a fact source for
 * six triggers; every other trigger has none, and a rule on one is now refused at
 * `POST /alert-rules` for the reason QA-F gave: *"a rule an administrator configured,
 * that is `active`, and that can never fire because nothing feeds it, is exactly the
 * thing that reads as 'configured and quiet' on a screen."* This record is that
 * sentence made mechanical — a trigger with no fields cannot have a condition written for it
 * here, so the form does not offer to create one, and says which of the two it is.
 *
 * Each list is the `fields` its loader actually publishes, transcribed field by field.
 * Nothing is invented: a name that is not measured would be evaluated as
 * `field_absent` and produce a rule that is inert while reading as configured.
 *
 * **No field here is a productivity score, a target or a quota, and R18/KTD2 say none
 * ever may be.** `alert-rules.test.tsx` asserts it over every entry rather than
 * trusting this comment, because the guardrail has to survive the next person who adds
 * a fact source.
 */
export const ALERT_TRIGGER_FIELDS: Readonly<Record<AlertTrigger, readonly ConditionField[]>> = {
  // `agentOfflineFacts` — one fact per device expected to report.
  agent_offline: [
    numberField('offline_minutes'),
    enumField('liveness', LIVENESS_VALUES),
    enumField('device_status', DEVICE_STATUS_VALUES),
    booleanField('never_reported'),
  ],
  // `unknownDeviceFacts` — one fact per device still awaiting approval.
  unknown_device: [
    numberField('pending_minutes'),
    enumField('device_status', DEVICE_STATUS_VALUES),
    booleanField('claimed_by_employee'),
  ],
  // `missingHoursFacts` — one fact per employee over the window.
  missing_hours: [
    numberField('missing_minutes'),
    numberField('days_counted'),
    numberField('days_withheld'),
  ],
  // `extendedIdleFacts` — one fact per employee, from Task 36's daily aggregates.
  // `private_minutes` is `detail`, not a field: it is reported so a reader can see
  // that private time was NOT counted as idle, and it is not something to alert on.
  extended_idle: [
    numberField('idle_minutes'),
    numberField('online_minutes'),
    numberField('days_counted'),
  ],
  // `lateArrivalFacts` / `earlyLeaveFacts` — one fact per employee over the window,
  // counted in DAYS. The minute figures are offered beside the day count because a
  // sum of minutes cannot tell one overslept morning from ten three-minute ones, and
  // a rule that cannot tell those apart fires at the wrong person.
  late_arrival: [
    numberField('late_days'),
    numberField('late_minutes_max'),
    numberField('late_minutes_total'),
    numberField('days_counted'),
    numberField('days_withheld'),
  ],
  early_leave: [
    numberField('early_leave_days'),
    numberField('early_leave_minutes_max'),
    numberField('early_leave_minutes_total'),
    numberField('days_counted'),
    numberField('days_withheld'),
  ],
  // The twelve with no fact source in this build. `POST /alert-rules` refuses these
  // outright (`details.reason: trigger_not_supported`), which is why the form offers
  // no create control for them rather than sending a request that cannot succeed.
  no_active_task: [],
  estimate_exceeded: [],
  productivity_drop: [],
  workload_overload: [],
  workload_underload: [],
  agent_stopped: [],
  agent_uninstalled: [],
  usb_unapproved: [],
  bulk_file_operation: [],
  blocked_app: [],
  blocked_website: [],
  after_hours_activity: [],
};

/**
 * The guardrail, as a predicate rather than as a promise — R18 and KTD2.
 *
 * *"No monitoring screen ranks employees, counts 'violations', or renders a quota"*,
 * and *"labour-cap warnings are soft and never a score input or ranking"*. A condition
 * on a productivity score with `>=` is a quota with an alert attached: it turns an
 * activity measurement into a target somebody is judged against, which is the one
 * thing this product's stance forbids.
 *
 * The form cannot build one today because `conditions[].field` is a **select** over
 * `ALERT_TRIGGER_FIELDS` and no vocabulary above names such a field. This predicate is
 * one more net under it: the test runs it over every offered field, so a fact source
 * that published `productivity_score` would turn this file red rather than quietly
 * putting a quota in a dropdown.
 *
 * ── It is a keyword denylist, and a green suite is not clearance ───────────
 *
 * Fix round 1 says this out loud rather than leaving the next author to infer it. The
 * pattern below catches `productivity_score`; it does **not** catch `efficiency_index`,
 * `output_per_hour`, `utilisation_pct` or any other careful name for the same idea. The
 * real guarantee is the one above it — **a human writes `ALERT_TRIGGER_FIELDS` by
 * hand**, one entry per fact source, and the question to ask when adding one is whether
 * the field is a measurement of a system or a target for a person. This predicate is a
 * tripwire for the obvious spelling, not a filter that makes the answer safe.
 *
 * The **backend permits a quota either way** — `conditions[].field` is any lower-case
 * identifier and the comparison operators take any number — which the U16 report
 * records as a finding, and which the branch's residual register now carries as an
 * ordering constraint: the per-trigger allow-list at the write path must land before or
 * with the first productivity fact source.
 */
export function isQuotaShapedField(field: string): boolean {
  return /score|quota|target|rank|violation|productivity/.test(field);
}

/* ──────────────────────────────────────────── the per-action parameter registry */

/** One parameter of one action type. Rendered by its own control, never free text. */
export interface AlertActionParameter {
  readonly key: string;
  readonly kind: ConditionFieldKind;
  readonly options: readonly string[];
}

/**
 * What each action type takes as parameters, and today every answer is **none**.
 *
 * That is a fact about the backend, not a gap in this file: `wp_alert_rules.actions` is
 * a `text[]` under `wp_alert_rules_actions_ck` and `AlertAction` is a bare string enum,
 * so an action is a key and nothing else — there is no parameter channel on the wire
 * for a recipient, a project, a priority or a template. `automation/actions.ts` derives
 * everything an action needs at fire time: the manager and IT recipients from the org
 * (`resolveManagerRecipients`, `resolveItRecipients`), the achau task body and its
 * priority from the alert and the rule's own severity (`payload.ts`).
 *
 * The registry exists anyway, exhaustive over the enum, so that the **field list** for a
 * parameterised action lands in one place — and so that `alert-rules.test.tsx` can
 * assert the rendered fields per type from this record rather than from a list retyped
 * in the test. `AlertRulesScreen` renders a parameter region for an action only when its
 * list is non-empty, which is why the same test can prove that a type with no parameters
 * renders none.
 *
 * Fix round 1 narrows what that claims. It is not "the day an action gains a parameter
 * it renders itself and the change is done": the control would render, but it holds no
 * state, and neither `AlertRuleDraft` nor `AlertRuleCreateRequest` carries a parameter,
 * so its value would still go nowhere. A parameterised action needs a wire shape first —
 * `actions` is `text[]` — and then a draft key, a create body member and a control that
 * writes to them. This record is where the field list lands, and that is all.
 */
export const ALERT_ACTION_PARAMETERS: Readonly<
  Record<AlertAction, readonly AlertActionParameter[]>
> = {
  notify_manager: [],
  notify_it: [],
  notify_hr: [],
  create_incident: [],
  create_achau_task: [],
};

/**
 * Which of the five this build actually performs, from `automation/actions.ts`'s own
 * header. Rendered beside each action so a rule is not configured with one on the
 * belief that somebody is being told.
 *
 *   `create_achau_task`, `notify_manager`, `notify_it`  performed by Task 52.
 *   `create_incident`   performed **elsewhere** — WP-L3 opens the incident inside the
 *                       transaction that writes the alert, against the company's
 *                       `incident_auto_create_min_severity`. Adding it here would give
 *                       one alert two incidents.
 *   `notify_hr`         **sends nothing.** `NOTIFY_KINDS` is a closed set with no HR
 *                       member; a rule carrying it is stored, valid, and logged at
 *                       `warn` each time it fires. Read it as "nobody has been told
 *                       yet", never as "HR was told".
 */
export const ALERT_ACTION_PERFORMED: Readonly<Record<AlertAction, boolean>> = {
  notify_manager: true,
  notify_it: true,
  notify_hr: false,
  create_incident: false,
  create_achau_task: true,
};
